<template>
  <div id="page-top"></div>
  <div
    class="md:max-w-[1540px] w-full mx-auto ui-scrollbar relative grid grid-cols-1 md:grid-cols-[258px_1fr] xl:grid-cols-[258px_1fr_321px] md:gap-x-6 justify-start px-4"
    :class="!isDraft ? 'grid-rows-[auto_1fr]' : 'grid-rows-[auto_auto_1fr]'"
    @click="closeSortDropdown"
  >
    <div class="md:col-span-2 xl:col-span-3 mt-8" v-if="isDraft">
      <div
        class="text-white text-base font-semibold leading-[130%] px-5 h-[52px] bg-primary rounded-2xl flex items-center justify-between"
      >
        Редактирование черновика №{{ draftId }}

        <button @click="clearDraft">
          <ICross class="size-4 text-white" />
        </button>
      </div>
    </div>

    <!-- Шапка: заголовок, платформы, сортировка и формат -->
    <div class="md:col-span-2 xl:col-span-3 mt-8 max-md:mt-6 mb-4 h-fit">
      <h1 class="text-2xl font-bold leading-8 text-txmain max-md:text-lg max-md:font-medium max-md:leading-[1.4]">
        Каталог каналов Telegram, Max и VK
      </h1>
      <p class="mt-2 text-base leading-normal text-txmain/60 max-md:mt-1 max-md:text-xs">
        Выберите каналы, добавьте в корзину и отправьте заказ за 2-3 минуты.
      </p>

      <div class="mt-4 flex items-center gap-6 max-md:mt-5 max-md:flex-col max-md:items-stretch max-md:gap-4 xl:pr-[345px]">
        <div class="flex items-center gap-2 max-md:grid max-md:grid-cols-3">
          <button
            v-for="platform in platforms"
            :key="platform.value"
            type="button"
            class="h-10 rounded-xl px-2 text-xs font-medium leading-normal transition-colors md:w-[81px]"
            :class="selectedPlatform === platform.value ? 'bg-primary text-white' : 'bg-white text-txmain hover:bg-primary/10'"
            @click="selectedPlatform = platform.value"
          >
            {{ platform.label }}
          </button>
        </div>

        <div class="flex flex-1 items-center justify-between gap-2">
          <!-- Десктоп: селект сортировки -->
          <div class="flex items-center gap-2 max-md:hidden">
            <span class="text-[13px] text-txmain/60">Сортировка:</span>
            <CatalogSortButtons
              :sorted-fields="sortedFields"
              :sorted-by="sortedBy"
              :order-by="orderBy"
              @sort="handleSort"
            />
          </div>
          <!-- Мобильный: направление + поле сортировки -->
          <div class="flex h-[30px] items-center gap-1 rounded-lg bg-primary/[0.08] px-2 md:hidden">
            <button
              type="button"
              class="flex items-center"
              @click="
                handleSort({
                  field: sortedBy ? sortedBy : 'view24h',
                  order: orderBy ? (orderBy === 'desc' ? 'asc' : orderBy === 'asc' ? null : 'desc') : 'desc',
                })
              "
            >
              <Icon name="basil:arrow-up-solid" class="text-base" :class="orderBy === 'desc' ? 'text-primary' : 'text-primary/40'" />
              <Icon name="basil:arrow-down-solid" class="-ml-1 text-base" :class="orderBy === 'asc' ? 'text-primary' : 'text-primary/40'" />
            </button>
            <div class="relative" @click.stop>
              <button
                type="button"
                class="text-[13px] leading-5 text-txmain"
                @click="isSortDropdownOpen = !isSortDropdownOpen"
              >
                {{ currentSortField }}
              </button>
              <div
                v-if="isSortDropdownOpen"
                class="absolute left-0 top-full z-50 mt-1 min-w-[160px] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
              >
                <button
                  v-for="field in sortedFields"
                  :key="field.name"
                  type="button"
                  class="flex w-full items-center justify-between px-3 py-2 text-left text-[13px] transition-colors hover:bg-gray-50"
                  :class="{ 'bg-primary/5 text-primary': sortedBy === field.name }"
                  @click="selectSortField(field.name)"
                >
                  {{ field.title }}
                  <Icon v-if="sortedBy === field.name" name="basil:check-solid" class="text-xs text-primary" />
                </button>
              </div>
            </div>
          </div>

          <CatalogFormatSelector v-model="placeFormat" :prices="prices" />
        </div>
      </div>
    </div>

    <!-- Фильтры (десктоп) -->
    <aside class="hidden md:block">
      <div class="catalog-filters sticky top-[114px] max-h-[calc(100dvh-130px)] space-y-4 overflow-y-auto rounded-[20px] bg-white p-4">
        <div class="flex items-center justify-between gap-2">
          <h2 class="text-xl font-bold leading-[1.05] text-txmain">Фильтры</h2>
          <span class="text-sm text-txmain/60">{{ totalCount }} {{ getChannelWord(totalCount) }}</span>
        </div>

        <UINewInput
          v-model="search"
          placeholder="Ссылка, название, описание"
          variant="light-primary"
          input-classes="!bg-bgnew !rounded-xl !h-11"
          custom-wrapper-classes="!mt-0"
          :is-border="false"
        >
          <template #postfix>
            <ISearch class="size-5 !stroke-txsecondary" />
          </template>
        </UINewInput>

        <div>
          <div class="flex items-end justify-between">
            <span class="text-sm text-txmain/60">Категория</span>
            <button v-if="category.length > 0" class="text-xs text-txred/80" @click="clearCategory">Сбросить</button>
          </div>
          <UINewTreeSelect
            ref="categorySelectRef"
            class="mt-2"
            v-model="category"
            :data="categories"
            :placeholder="categoryPlaceholder"
            multiple
            clearable
            filterable
            show-checkbox
            :is-border="false"
            deferred-update
            @visible-change="handleCategoryVisibleChange"
          />
        </div>

        <div>
          <div class="flex items-end justify-between">
            <span class="text-sm text-txmain/60">Подписчики</span>
            <button
              v-if="rangeSubscribers[0] !== minSubscribers || rangeSubscribers[1] !== maxSubscribers"
              class="text-xs text-txred/80"
              @click="rangeSubscribers = [minSubscribers, maxSubscribers]"
            >
              Сбросить
            </button>
          </div>
          <UINewProgressiveRangeInput
            class="catalog-range mt-2"
            v-model="rangeSubscribers"
            :min="minSubscribers"
            :max="maxSubscribers"
          />
        </div>

        <div>
          <div class="flex items-end justify-between">
            <span class="text-sm text-txmain/60">Цена</span>
            <button
              v-if="isChangePrice"
              class="text-xs text-txred/80"
              @click="
                () => {
                  rangePrice = [minPrice, maxPrice];
                  nextTick(() => {
                    isChangePrice = false;
                  });
                }
              "
            >
              Сбросить
            </button>
          </div>
          <UINewPriceRangeInput class="catalog-range mt-2" v-model="rangePrice" :min="minPrice" :max="maxPrice" />
        </div>

        <template v-if="showAllFilters">
          <div>
            <div class="flex items-end justify-between">
              <span class="text-sm text-txmain/60">География</span>
              <button v-if="geography.length > 0" class="text-xs text-txred/80" @click="clearGeography">Сбросить</button>
            </div>
            <UINewMSelect
              ref="geographySelectRef"
              class="mt-2"
              v-model="geography"
              :options="geographyList"
              :placeholder="geographyPlaceholder"
              multiple
              clearable
              filterable
              deferred-update
              @visible-change="handleGeographyVisibleChange"
            />
          </div>

          <div>
            <div class="flex items-end justify-between">
              <span class="text-sm text-txmain/60">Пол</span>
              <button
                v-if="isChangeGender && inputsGender.length > 0"
                class="text-xs text-txred/80"
                @click="
                  () => {
                    inputsGender = [];
                    rangeGender = [];
                    nextTick(() => {
                      isChangeGender = false;
                    });
                  }
                "
              >
                Сбросить
              </button>
            </div>
            <UINewSliderInput
              class="catalog-range mt-2 !w-full"
              v-model="rangeGender"
              :min="minGender"
              :max="maxGender"
              from-label="Муж"
              to-label="Жен"
              :is-range="false"
              from-padding="50px"
              to-padding="50px"
            />
          </div>

          <div>
            <div class="flex items-end justify-between">
              <span class="text-sm text-txmain/60">Возраст</span>
              <button
                v-if="rangeAge[0] !== 0 || rangeAge[1] !== 100"
                class="text-xs text-txred/80"
                @click="rangeAge = [0, 100]"
              >
                Сбросить
              </button>
            </div>
            <UINewRangeInput class="catalog-range mt-2" v-model="rangeAge" :min="minAge" :max="maxAge" />
          </div>
        </template>

        <button
          type="button"
          class="w-full rounded-2xl bg-[#EFF4FF] py-3 text-center text-sm text-primary transition-colors hover:bg-primary/15"
          @click="showAllFilters = !showAllFilters"
        >
          {{ showAllFilters ? "Скрыть фильтры" : `Все фильтры${hiddenFiltersCount ? ` (${hiddenFiltersCount})` : ""}` }}
        </button>
        <button
          type="button"
          class="w-full rounded-2xl border border-[#D9E5FF] py-3 text-center text-sm text-primary transition-colors hover:bg-primary/5"
          @click="resetFilters"
        >
          Очистить
        </button>
      </div>
    </aside>

    <div class="w-full min-w-0 flex flex-col">
      <!-- Поиск и фильтры (мобильный) -->
      <div class="catalog-filters mb-4 rounded-2xl bg-white p-3 md:hidden">
        <div class="flex items-center gap-2">
          <UINewInput
            v-model="search"
            placeholder="Ссылка, название, описание"
            variant="light-primary"
            input-classes="!bg-bgnew !rounded-lg !h-10 !text-xs"
            custom-wrapper-classes="!mt-0"
            class="flex-1"
            :is-border="false"
          >
            <template #postfix>
              <ISearch class="size-5 !stroke-txsecondary" />
            </template>
          </UINewInput>
          <button
            type="button"
            class="flex h-10 shrink-0 items-center gap-2 rounded-lg bg-primary px-3 text-xs text-white"
            @click="toggleMobileFilters"
          >
            Фильтры
            <span
              v-if="activeFiltersCount"
              class="flex size-4 items-center justify-center rounded-full bg-white/10 !text-[10px] !text-white"
            >
              {{ activeFiltersCount }}
            </span>
          </button>
        </div>

        <div class="mt-3">
          <div class="flex items-end justify-between">
            <span class="text-xs leading-5 text-txmain/60">Категория</span>
            <button v-if="category.length > 0" class="text-xs text-txred/80" @click="clearCategory">Сбросить</button>
          </div>
          <UINewTreeSelect
            ref="categorySelectMobileRef"
            class="mt-1"
            v-model="category"
            :data="categories"
            :placeholder="categoryPlaceholder"
            multiple
            clearable
            filterable
            show-checkbox
            :is-border="false"
            deferred-update
            @visible-change="handleCategoryVisibleChange"
          />
        </div>

        <template v-if="isOpenMobileFilters">
          <div class="my-4 h-px bg-[#1B1C1E]/[0.06]" />

          <div class="space-y-4">
            <div>
              <div class="flex items-end justify-between">
                <span class="text-xs leading-5 text-txmain/60">Подписчики</span>
                <button
                  v-if="rangeSubscribers[0] !== minSubscribers || rangeSubscribers[1] !== maxSubscribers"
                  class="text-xs text-txred/80"
                  @click="rangeSubscribers = [minSubscribers, maxSubscribers]"
                >
                  Сбросить
                </button>
              </div>
              <UINewProgressiveRangeInput
                class="catalog-range mt-1"
                v-model="rangeSubscribers"
                :min="minSubscribers"
                :max="maxSubscribers"
              />
            </div>

            <div>
              <div class="flex items-end justify-between">
                <span class="text-xs leading-5 text-txmain/60">Цена</span>
                <button
                  v-if="isChangePrice"
                  class="text-xs text-txred/80"
                  @click="
                    () => {
                      rangePrice = [minPrice, maxPrice];
                      nextTick(() => {
                        isChangePrice = false;
                      });
                    }
                  "
                >
                  Сбросить
                </button>
              </div>
              <UINewPriceRangeInput class="catalog-range mt-1" v-model="rangePrice" :min="minPrice" :max="maxPrice" />
            </div>

            <div>
              <div class="flex items-end justify-between">
                <span class="text-xs leading-5 text-txmain/60">География</span>
                <button v-if="geography.length > 0" class="text-xs text-txred/80" @click="clearGeography">Сбросить</button>
              </div>
              <UINewMSelect
                ref="geographySelectMobileRef"
                class="mt-1"
                v-model="geography"
                :options="geographyList"
                :placeholder="geographyPlaceholder"
                multiple
                clearable
                filterable
                deferred-update
                @visible-change="handleGeographyVisibleChange"
              />
            </div>

            <div>
              <div class="flex items-end justify-between">
                <span class="text-xs leading-5 text-txmain/60">Пол</span>
                <button
                  v-if="isChangeGender && inputsGender.length > 0"
                  class="text-xs text-txred/80"
                  @click="
                    () => {
                      inputsGender = [];
                      rangeGender = [];
                      nextTick(() => {
                        isChangeGender = false;
                      });
                    }
                  "
                >
                  Сбросить
                </button>
              </div>
              <UINewSliderInput
                class="catalog-range mt-1 !w-full"
                v-model="rangeGender"
                :min="minGender"
                :max="maxGender"
                from-label="Муж"
                to-label="Жен"
                :is-range="false"
                from-padding="50px"
                to-padding="50px"
              />
            </div>

            <div>
              <div class="flex items-end justify-between">
                <span class="text-xs leading-5 text-txmain/60">Возраст</span>
                <button
                  v-if="rangeAge[0] !== 0 || rangeAge[1] !== 100"
                  class="text-xs text-txred/80"
                  @click="rangeAge = [0, 100]"
                >
                  Сбросить
                </button>
              </div>
              <UINewRangeInput class="catalog-range mt-1" v-model="rangeAge" :min="minAge" :max="maxAge" />
            </div>

            <button
              type="button"
              class="w-full rounded-lg border border-[#D9E5FF] py-3 text-center text-sm text-primary"
              @click="resetFilters"
            >
              Очистить
            </button>
          </div>
        </template>
      </div>

      <!-- Список (мобильный) -->
      <div
        class="md:hidden space-y-4"
        :class="{ 'flex items-center justify-center flex-1': places.length === 0 }"
      >
        <div
          v-if="places.length === 0 && !isLoading"
          class="text-txprimary text-lg mt-20 text-center flex-1 flex flex-col items-center justify-center bg-white rounded-3xl py-8 px-8 gap-5"
        >
          Ничего не найдено
          <button
            @click="resetFilters"
            class="w-full text-primary text-sm bg-primary/10 md:bg-[#EFF4FF] text-center rounded-2xl py-3 mt-2"
          >
            Сбросить фильтры
          </button>
        </div>
        <template v-else>
          <template v-for="place in places" :key="place.id">
            <catalog-place-card-mobile
              @save-to-collection="openSaveToCollection"
              :place="place"
              :is-in-basket="
                !!basket.find(
                  (item) =>
                    item.id === place.id &&
                    (item.format?.priceType === place.format?.priceType ||
                      (!item.format && !place.format))
                )
              "
              :basket-quantity="
                basket.find(
                  (item) =>
                    item.id === place.id &&
                    (item.format?.priceType === place.format?.priceType ||
                      (!item.format && !place.format))
                )?.quantity || 0
              "
              @add-to-basket="addPlaceToBasket"
              @decrease-quantity="handleDecreaseQuantity"
              @increase-quantity="handleIncreaseQuantity"
              @follow="onFollow"
              @open-place="openPlace"
              @message="askQuestion"
            />
          </template>
        </template>
      </div>

      <!-- Список (десктоп) -->
      <div
        class="hidden md:block space-y-3 md:mb-6"
        :class="{ 'flex items-center justify-center flex-1': places.length === 0 }"
      >
        <div
          v-if="places.length === 0 && !isLoading"
          class="text-lg text-center flex-1 flex flex-col items-center justify-center py-[30px] rounded-2xl bg-white max-w-[500px] md:mx-auto md:mt-5"
        >
          Ничего не найдено
          <button
            @click="resetFilters"
            class="text-primary text-sm bg-[#EFF4FF] text-center rounded-2xl py-3 mt-5 w-[226px]"
          >
            Сбросить фильтры
          </button>
        </div>
        <template v-else>
          <CatalogPlaceCard
              @save-to-collection="openSaveToCollection"
            v-for="place in places"
            :key="place.id"
            :place="place"
            :is-in-basket="
              !!basket.find(
                (item) =>
                  item.id === place.id &&
                  (item.format?.priceType === place.format?.priceType ||
                    (!item.format && !place.format))
              )
            "
            :basket-quantity="
              basket.find(
                (item) =>
                  item.id === place.id &&
                  (item.format?.priceType === place.format?.priceType ||
                    (!item.format && !place.format))
              )?.quantity || 0
            "
            @open-place="openPlace"
            @add-to-basket="addPlaceToBasket"
            @decrease-quantity="handleDecreaseQuantity"
            @increase-quantity="handleIncreaseQuantity"
            @follow="onFollow"
            @message="askQuestion"
          />
        </template>
      </div>
      <div ref="loadMore"></div>
    </div>

    <CatalogCartSidebar />

    <div class="col-span-1 md:col-span-2 xl:col-span-3 w-full" v-if="!isLoading">
      <PublicFooter />
    </div>
  </div>

  <ClientOnly>
    <Teleport to="body">
      <el-drawer
        v-model="isModalPlaceOpen"
        :with-header="false"
        :size="isDesktop ? '788px' : '100dvw'"
        align-center
        class="!bg-bgnew"
        body-class="!p-0"
        :lock-scroll="false"
      >
        <CatalogChannelSidebar v-if="isModalPlaceOpen" @close="isModalPlaceOpen = false" @message="askQuestion" />
      </el-drawer>
      <ChatDirectDialog v-if="directChatPlace" :place="directChatPlace" @close="directChatPlace = null" />
    </Teleport>
  </ClientOnly>

  <Transition name="scroll-to-top">
    <button
      v-if="showScrollToTop"
      @click="scrollToTop"
      class="scroll-to-top-btn"
      :style="{
        bottom: !isDesktop
          ? (isAuth
              ? (basket.length > 0 ? '144px' : '80px')
              : (basket.length > 0 ? '84px' : '20px'))
          : (basket.length > 0 ? '84px' : '20px'),
      }"
      aria-label="Прокрутить наверх"
    >
      <Icon name="basil:arrow-up-solid" class="text-xl text-white" />
    </button>
  </Transition>
  <CollectionsSaveToModal v-model:open="isSaveToCollectionOpen" :places="saveToCollectionPlaces" />
</template>

<script setup lang="ts">
  import { ApiKey } from "~/common/API/authToken";
  import type { ReducedPlace, PlacePrice, Place } from "@/interfaces/publisher";
  import PlaceService from "@/services/PlaceService";
  import { toPlaceRef, type CollectionPlaceRef } from "~/services/CollectionService";
  import OtherService from "@/services/OtherService";
  import BasketService from "@/services/BasketService";

  import ISearch from "~/components/UINew/icons/ISearch.vue";

  import { useUserStore } from "~/store/UserModule";
  import { useScreenSize } from "@/helpers/isMobile";
  import { useBasketStore } from "~/store/BasketModule";
  import ICross from "~/components/UINew/icons/ICross.vue";

  const { isDesktop, isXl } = useScreenSize();

  const isSaveToCollectionOpen = ref(false);
  const saveToCollectionPlaces = ref<CollectionPlaceRef[]>([]);
  const openSaveToCollection = (place: any) => {
    saveToCollectionPlaces.value = [toPlaceRef(place)];
    isSaveToCollectionOpen.value = true;
  };

  interface Category {
    id: number;
    name: string;
    children: Category[];
  }

  interface Country {
    id: number;
    ru: string;
    en: string;
    title: string;
    value: string;
    label: string;
  }

  const route = useRoute();
  const userModule = useUserStore();

  // SEO Meta теги
  useSeoMeta({
    title: "Каталог каналов Telegram, MAX и VK для рекламы | Найти площадку — Telepost",
    description:
      "Каталог каналов Telegram, MAX и VK для размещения рекламы на бирже Telepost. Более 20 тематик, фильтры по подписчикам, цене, географии и аудитории. Автоматизация маркировки.",
    keywords:
      "каталог каналов телеграм, каталог каналов MAX, добавить канал MAX в каталог, найти ТГ каналы для рекламы, где найти рекламу для телеграмм канала, каналы для размещения рекламы, телеграмм канал для размещения рекламы",
    ogTitle: "Каталог каналов Telegram, MAX и VK для рекламы | Найти площадку — Telepost",
    ogDescription:
      "Каталог каналов Telegram, MAX и VK для размещения рекламы на бирже Telepost. Более 20 тематик, фильтры по подписчикам, цене, географии и аудитории. Автоматизация маркировки.",
    ogUrl: "https://telepost.pro/catalog",
    ogSiteName: "Telepost",
    ogType: "website",
    ogLocale: "ru_RU",
    twitterCard: "summary_large_image",
    twitterTitle: "Каталог каналов Telegram, MAX и VK для рекламы | Найти площадку — Telepost",
    twitterDescription:
      "Каталог каналов Telegram, MAX и VK для размещения рекламы на бирже Telepost. Более 20 тематик, фильтры по подписчикам, цене, географии и аудитории. Автоматизация маркировки.",
  });

  const selectedPlatform = ref<"telegram" | "vkontakte" | "max">("telegram");
  const search = ref<string>("");
  const places = ref<ReducedPlace[]>([]);
  const totalCount = ref<number>(0);
  const pageCount = ref<number>(1);

  const loadMore = ref<any>(null);
  const paginationPage = ref<number>(1);

  const isOpenMobileFilters = ref<boolean>(false);

  const basketStore = useBasketStore();
  const { basket, isDraft, draftId } = storeToRefs(basketStore);

  const isModalPlaceOpen = ref<boolean>(false);

  const prices = [
    {
      label: "Все",
      value: "all",
    },
    {
      label: "Пост 24ч",
      value: "post24h",
    },
    {
      label: "Пост 48ч",
      value: "post48h",
    },
    {
      label: "Пост 72ч",
      value: "post72h",
    },
    {
      label: "Пост 30 дней",
      value: "post30d",
    },
    {
      label: "Репост",
      value: "repost",
    },
    {
      label: "Сторис",
      value: "story",
    },
  ];

  const categories = ref<Category[]>([]);
  const geographyList = ref<Country[]>([]);

  const category = ref<number[]>([]);
  const geography = ref<string[]>([]);

  // Refs для компонентов
  const categorySelectRef = ref();
  const categorySelectMobileRef = ref();
  const geographySelectRef = ref();
  const geographySelectMobileRef = ref();
  const minSubscribers = ref<number>(0);
  const maxSubscribers = ref<number>(0);
  const rangeSubscribers = ref<number[]>([0, 0]);
  const minPrice = ref<number>(0);
  const maxPrice = ref<number>(0);
  const rangePrice = ref<number[]>([0, 0]);
  const inputsGender = ref<number[]>([]);
  const rangeGender = ref<number[]>([]);
  const minGender = ref<number>(0);
  const maxGender = ref<number>(100);
  const rangeAge = ref<number[]>([0, 100]);
  const minAge = ref<number>(0);
  const maxAge = ref<number>(100);
  const placeFormat = ref<string>("all");
  const isChangeGender = ref<boolean>(false);
  const isChangePrice = ref<boolean>(false);

  const platforms: { value: "telegram" | "vkontakte" | "max"; label: string }[] = [
    { value: "telegram", label: "Telegram" },
    { value: "vkontakte", label: "ВКонтакте" },
    { value: "max", label: "MAX" },
  ];

  // Десктоп: география, пол и возраст спрятаны за «Все фильтры»
  const showAllFilters = ref<boolean>(false);
  const isGenderActive = computed(() => isChangeGender.value && inputsGender.value.length > 0);
  const isAgeActive = computed(() => rangeAge.value[0] !== 0 || rangeAge.value[1] !== 100);
  const hiddenFiltersCount = computed(
    () => [geography.value.length > 0, isGenderActive.value, isAgeActive.value].filter(Boolean).length
  );
  const activeFiltersCount = computed(
    () =>
      [
        category.value.length > 0,
        rangeSubscribers.value[0] !== minSubscribers.value ||
          rangeSubscribers.value[1] !== maxSubscribers.value,
        isChangePrice.value,
      ].filter(Boolean).length + hiddenFiltersCount.value
  );

  // Вопрос каналу до заказа: ручки на бэке пока нет, без входа — к авторизации
  const directChatPlace = ref<ReducedPlace | null>(null);
  const askQuestion = (place: ReducedPlace) => {
    if (!isAuth.value) {
      userModule.loginDialogVisible = true;
      return;
    }
    directChatPlace.value = place;
  };
  const isRefresh = ref<boolean>(false);
  const isUpdatingPriceFromResponse = ref<boolean>(false);
  const isLoading = ref<boolean>(false);
  const sortedBy = ref<string | null>(null);
  const orderBy = ref<"asc" | "desc" | null>(null);
  const isSortDropdownOpen = ref(false);
  const categoryPlaceholder = ref<string>("Выбрать");
  const geographyPlaceholder = ref<string>("Выбрать");

  const handleSort = (payload: { field: string | null; order: "asc" | "desc" | null }) => {
    sortedBy.value = payload.field;
    orderBy.value = payload.order;
    getPlaces(true);
  };

  // Функция для очистки категорий
  const clearCategory = () => {
    category.value = [];

    // Очищаем также внутреннее состояние компонентов с deferredUpdate
    if (categorySelectRef.value?.clearSelection) {
      categorySelectRef.value.clearSelection();
    }
    if (categorySelectMobileRef.value?.clearSelection) {
      categorySelectMobileRef.value.clearSelection();
    }
  };

  // Функция для очистки географии
  const clearGeography = () => {
    geography.value = [];

    // Очищаем также внутреннее состояние компонентов с deferredUpdate
    if (geographySelectRef.value?.clearSelection) {
      geographySelectRef.value.clearSelection();
    }
    if (geographySelectMobileRef.value?.clearSelection) {
      geographySelectMobileRef.value.clearSelection();
    }
  };

  const selectSortField = (fieldName: string) => {
    sortedBy.value = fieldName;
    isSortDropdownOpen.value = false;
    handleSort({
      field: fieldName,
      order: orderBy.value || "desc",
    });
  };

  // Закрытие dropdown при клике вне его
  const closeSortDropdown = () => {
    isSortDropdownOpen.value = false;
  };

  const handleCategoryVisibleChange = (visible: boolean) => {
    categoryPlaceholder.value = visible ? "Поиск по названию" : "Выбрать";
  };

  const handleGeographyVisibleChange = (visible: boolean) => {
    geographyPlaceholder.value = visible ? "Поиск по названию" : "Выбрать";
  };

  const sortedFields = computed(() => [
    {
      name: "view24h",
      title: "Просмотры",
    },
    {
      name: "subscribers_statistic_count",
      title: "Подписчики",
    },
    {
      name: "created_at",
      title: "Дата добавления",
    },
    {
      name: placeFormat.value !== "all" ? placeFormat.value : "post24h",
      title: "Стоимость",
    },
  ]);

  const currentSortField = computed(() => {
    const field = sortedFields.value.find((f) => f.name === sortedBy.value);
    return field ? field.title : "Просмотры";
  });

  const toggleMobileFilters = () => {
    isOpenMobileFilters.value = !isOpenMobileFilters.value;
  };

  const timer = ref<NodeJS.Timeout | null>(null);

  watch(
    [
      search,
      category,
      geography,
      () => rangeSubscribers.value,
      () => rangePrice.value,
      rangeGender,
      rangeAge,
    ],
    () => {
      if (!isRefresh.value) {
        return;
      }
      if (timer.value) {
        clearTimeout(timer.value);
      }
      timer.value = setTimeout(() => {
        getPlaces(true);
      }, 1000);
    },
    {
      deep: true,
    }
  );

  const isTemplate = computed(() => route.query.draft);

  watch(rangeGender, (value) => {
    if (value.length > 0) {
      inputsGender.value[0] = value[0];
      inputsGender.value[1] = value[1];
      isChangeGender.value = true;
    }
  });

  watch(rangePrice, (value) => {
    // Пропускаем обновление, если цена обновляется программно из ответа API
    if (isUpdatingPriceFromResponse.value) {
      return;
    }
    if (
      value.length > 0 &&
      maxPrice.value > 0 &&
      (value[0] !== minPrice.value || value[1] !== maxPrice.value)
    ) {
      isChangePrice.value = true;
    }
  });

  watch(placeFormat, (value, oldValue) => {
    // Обновляем форматы в существующих местах
    places.value = places.value.map((place: ReducedPlace) => {
      if (value === "all") {
        // Если выбран "Все", выбираем первый доступный прайс
        const firstAvailablePrice =
          place.prices && place.prices.length > 0 ? place.prices[0] : null;
        return {
          ...place,
          format: firstAvailablePrice,
        };
      } else {
        const matchingPrice = place.prices.find((price) => price.priceType === value);

        if (!matchingPrice) {
          return {
            ...place,
            format: null,
          };
        } else {
          return {
            ...place,
            format: matchingPrice,
          };
        }
      }
    });

    // Если была активна сортировка по старому формату - обновляем на новый
    if (sortedBy.value === oldValue && value !== "all") {
      sortedBy.value = value;
    }

    // Временно отключаем isRefresh чтобы избежать бесконечного цикла при смене формата
    isRefresh.value = false;
    getPlaces(true);
    nextTick(() => {
      isRefresh.value = true;
    });
  });

  watch(selectedPlatform, () => {
    // Временно отключаем isRefresh чтобы избежать двойного запроса
    isRefresh.value = false;
    // Фильтры при смене площадки сохраняем — перезапрашиваем выдачу с теми же
    // условиями. Сбросить их можно кнопкой «Сбросить фильтры».
    getPlaces(true);
    nextTick(() => {
      isRefresh.value = true;
    });
  });

  // const updateBusket = (place: Place, format: PlacePrice) => {
  // places.value = places.value.map((p: ReducedPlace) =>
  //   p.id === place.id ? { ...p, format } : p
  // );
  // };

  // const removePlace = (place: Place) => {
  //   places.value = places.value.filter((p) => p.id !== place.id);
  // };

  const resetFilters = () => {
    search.value = "";
    rangeSubscribers.value = [minSubscribers.value, maxSubscribers.value];
    rangePrice.value = [minPrice.value, maxPrice.value];
    inputsGender.value = [];
    rangeGender.value = [];
    rangeAge.value = [0, 100];
    category.value = [];
    geography.value = [];
    nextTick(() => {
      isChangeGender.value = false;
      isChangePrice.value = false;
    });
  };

  const getBasket = () => {
    const basketData = BasketService.getBasket();
    if (basketData) {
      const places = basketData.places;
      if (places) {
        // Создаем полностью независимые копии объектов из localStorage
        basket.value = places.map((place: any) => {
          const cleanPlace = JSON.parse(JSON.stringify(place));
          if (!("quantity" in cleanPlace)) cleanPlace.quantity = 1;
          if (!("format" in cleanPlace)) cleanPlace.format = null;
          return cleanPlace;
        });
      } else {
        basket.value = [];
      }
    }
  };

  const getFilters = async () => {
    // const response = await OtherService.getFilters();
    // console.log(response);
  };

  const removePlace = (place: ReducedPlace) => {
    basket.value = basket.value.filter(
      (p: ReducedPlace) =>
        !(
          p.id === place.id &&
          (p.format?.priceType === place.format?.priceType || (!p.format && !place.format))
        )
    );
  };

  const clearBasket = () => {
    basket.value = [];
    // Полная очистка корзины = новый заказ → сбрасываем весь локальный черновик.
    basketStore.campaignName = "";
    if (import.meta.client) {
      localStorage.removeItem("orderId");
      localStorage.setItem("basketType", "0");
      // Очищаем состояние общего времени при очистке корзины
      localStorage.removeItem("multiScheduleState");
      localStorage.removeItem("orderScheduleStateV2");
      localStorage.removeItem("scheduleId");
      localStorage.removeItem("scheduleIds");
      localStorage.removeItem("campaignName");
      localStorage.removeItem("post");
      localStorage.removeItem("creativeId");
      localStorage.removeItem("creativeUuid");
      localStorage.removeItem("creativeTemporaryToken");
      localStorage.removeItem("creativeFiles");
      // Сентинел: на следующем mount шага «Пост» проигнорировать любые остатки креатива.
      localStorage.setItem("postCleared", "1");
    }
  };

  const handleDecreaseQuantity = (place: ReducedPlace) => {
    // Находим элемент в корзине с ТЕКУЩИМ выбранным форматом
    const basketItem = basket.value.find(
      (item) =>
        item.id === place.id &&
        (item.format?.priceType === place.format?.priceType || (!item.format && !place.format))
    );

    if (!basketItem) return;

    if (basketItem.quantity && basketItem.quantity > 1) {
      basketItem.quantity--;
    } else {
      // Удаляем этот конкретный item
      const index = basket.value.findIndex(
        (p: ReducedPlace) =>
          p.id === basketItem.id &&
          p.format?.priceType === basketItem.format?.priceType &&
          p === basketItem // ссылка на тот же объект
      );
      if (index !== -1) {
        basket.value.splice(index, 1);
      }
    }
  };

  const handleIncreaseQuantity = (place: ReducedPlace) => {
    // Находим элемент в корзине с ТЕКУЩИМ выбранным форматом
    const basketItem = basket.value.find(
      (item) =>
        item.id === place.id &&
        (item.format?.priceType === place.format?.priceType || (!item.format && !place.format))
    );

    if (!basketItem) return;

    if (basketItem.quantity) {
      basketItem.quantity++;
    }
  };

  // getBasket() и getFilters() перенесены в onMounted для SSR-совместимости

  const getPlaces = async (reset = false) => {
    isLoading.value = true;
    if (reset) {
      paginationPage.value = 1;
    }
    const response: any = await PlaceService.getPlaces({
      ...(search.value ? { search: search.value } : {}),
      pageNumber: paginationPage.value,
      categories: category.value ? category.value : undefined,
      countries: geography.value ? geography.value : undefined,
      ...(rangeSubscribers.value[0] !== minSubscribers.value ||
      rangeSubscribers.value[1] !== maxSubscribers.value
        ? {
            subscribersTo: rangeSubscribers.value[1],
            subscribersFrom: rangeSubscribers.value[0],
          }
        : {}),
      ...(rangePrice.value[0] !== minPrice.value || rangePrice.value[1] !== maxPrice.value
        ? {
            priceTo: rangePrice.value[1],
            priceFrom: rangePrice.value[0],
          }
        : {}),
      // subscribersTo: rangeSubscribers.value[1],
      // subscribersFrom: rangeSubscribers.value[0],
      // ageFrom: rangeAge.value[0],
      // ageTo: rangeAge.value[1],

      ...(isChangeGender.value ? { genderPercent: inputsGender.value[0] } : {}),
      ...(sortedBy.value && orderBy.value
        ? {
            sortFields: [sortedBy.value],
            sortDirections: [orderBy.value],
          }
        : {}),
      ...(placeFormat.value !== "all" ? { priceType: placeFormat.value } : {}),
      platform: selectedPlatform.value,
    });
    console.log(response);
    if (reset) {
      places.value = [];
      totalCount.value = 0;
    }
    places.value = [
      ...places.value,
      ...response.records.map((place: ReducedPlace) => {
        if (placeFormat.value === "all") {
          // Если выбран "Все", выбираем первый доступный прайс
          const firstAvailablePrice =
            place.prices && place.prices.length > 0 ? place.prices[0] : null;
          return {
            ...place,
            format: firstAvailablePrice,
            quantity: 1,
          };
        } else {
          const matchingPrice = place.prices.find((price) => price.priceType === placeFormat.value);

          if (!matchingPrice) {
            return {
              ...place,
              format: null,
              quantity: 1,
            };
          } else {
            return {
              ...place,
              format: matchingPrice,
              quantity: 1,
            };
          }
        }
      }),
    ];
    totalCount.value = response.meta.totalCount;
    pageCount.value = response.meta.pageCount;

    // Обновляем максимальную цену из maxPrices, если он есть в ответе
    if (response.meta.maxPrices && typeof response.meta.maxPrices === "object") {
      const maxPricesValues = Object.values(response.meta.maxPrices) as number[];
      if (maxPricesValues.length > 0) {
        const maxPriceValue = Math.max(...maxPricesValues);
        if (maxPriceValue > 0 && maxPriceValue !== maxPrice.value) {
          // Обновляем диапазон только при первой загрузке или если пользователь не изменял цену
          if (reset || !isChangePrice.value) {
            // Временно отключаем watch на фильтры и устанавливаем флаг обновления цены
            isRefresh.value = false;
            isUpdatingPriceFromResponse.value = true;
            maxPrice.value = maxPriceValue;
            // Цену, заданную пользователем, не сбрасываем — только подрезаем под
            // новый максимум (у каждой площадки он свой)
            rangePrice.value = isChangePrice.value
              ? [
                  Math.min(rangePrice.value[0], maxPriceValue),
                  Math.min(rangePrice.value[1], maxPriceValue),
                ]
              : [minPrice.value, maxPriceValue];
            nextTick(() => {
              isUpdatingPriceFromResponse.value = false;
              isRefresh.value = true;
            });
          } else {
            // Если не обновляем диапазон, просто обновляем maxPrice
            maxPrice.value = maxPriceValue;
          }
        }
      }
    }

    isLoading.value = false;
  };

  const onLoadMore = () => {
    if (!places.value.length || pageCount.value <= paginationPage.value) return;
    paginationPage.value++;
    getPlaces();
  };

  const checkVisibilityFallback = () => {
    const rect = loadMore.value.getBoundingClientRect();
    const inView = rect.top >= 300 && rect.bottom <= window.innerHeight;

    if (inView) {
      onLoadMore();
      window.removeEventListener("scroll", checkVisibilityFallback);
    }
  };

  const initIntersectionObserver = () => {
    const target = loadMore.value;

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onLoadMore();
            // observer.unobserve(target)
          }
        });
      });

      observer.observe(target);
    } else {
      (window as Window).addEventListener("scroll", checkVisibilityFallback);
    }
  };

  const getCategories = async () => {
    isLoading.value = true;
    const response: any = (await OtherService.getCategories()) as {
      groupCategories: Category[];
    };
    categories.value = response.groupCategories.map((category: any) => ({
      ...category,
      label: category.name,
      value: category.id,
      children: category.categories.map((child: Category) => ({
        ...child,
        label: child.name,
        value: child.id,
      })),
    }));

    const route = useRoute();
    const categoryId = route.query.category;

    if (categoryId) {
      const findCategory = (categories: Category[]): number[] | undefined => {
        for (const cat of categories) {
          if (cat.id === +categoryId) {
            return cat.children.map((child) => child.id);
          }
          if (cat.children) {
            const found = findCategory(cat.children);
            if (found) return found;
          }
        }
      };

      const foundId = findCategory(categories.value);
      if (foundId) {
        category.value = foundId;
      }
    }
  };

  const getCountries = async () => {
    const response: any = (await OtherService.getCountries(250)) as {
      records: Country[];
    };
    geographyList.value = response.records.map((country: Country) => ({
      ...country,
      label: country.ru,
      value: country.id,
    }));
  };

  const addPlaceToBasket = (place: ReducedPlace) => {
    const existingPlaceWithFormat = basket.value.find(
      (item) =>
        item.id === place.id &&
        (item.format?.priceType === place.format?.priceType || (!item.format && !place.format))
    );

    if (existingPlaceWithFormat) {
      existingPlaceWithFormat.quantity = (existingPlaceWithFormat.quantity || 1) + 1;
      return;
    }

    // Создаем полностью независимую копию объекта для корзины
    const basketItem = JSON.parse(
      JSON.stringify({
        id: place.id,
        name: place.name,
        url: place.url,
        avatar: place.avatar,
        format: place.format,
        quantity: 1,
        // Копируем только необходимые поля, чтобы избежать циклических ссылок
        categories: place.categories,
        subscribersStatistic: place.subscribersStatistic,
        prices: place.prices,
        viewsStatistic: place.viewsStatistic,
        er: place.er,
      })
    );

    // @ts-ignore
    BasketService.addPlace(
      basketItem as ReducedPlace & {
        format: PlacePrice | null;
        subscribersStatistic: { count: number };
      }
    );

    basket.value.push(basketItem as ReducedPlace);
  };

  const openPlace = (slug: string) => {
    // if (isDesktop.value) {
    navigateTo({ query: { id: slug } });
    nextTick(() => {
      setTimeout(() => {
        isModalPlaceOpen.value = true;
      }, 100);
    });
    // } else {
    //   navigateTo(`/ch/${id}?s=${s}`, { open: { target: "_blank" } });
    // }
  };

  const onFollow = async (place: ReducedPlace) => {
    if (!isAuth.value) {
      userModule.loginDialogVisible = true;
      return;
    }
    if (place.isFavorite) {
      try {
        await PlaceService.removePlaceFromFavorite({ id: place.id, social: place.social || "telegram" });
        place.isFavorite = false;
      } catch (error) {
        console.error(error);
      }
    } else {
      try {
        await PlaceService.addPlaceToFavorite({ placeId: place.id, social: place.social || "telegram" });
        place.isFavorite = true;
      } catch (error) {
        console.error(error);
      }
    }
  };

  const getMinMaxFilters = async () => {
    const response: any = await OtherService.getMinMaxFilters();
    minSubscribers.value = +response.min;
    maxSubscribers.value = Math.ceil(+response.max / 100000) * 100000;
    rangeSubscribers.value = [+minSubscribers.value, +maxSubscribers.value];
  };

  const initFilters = async () => {
    isLoading.value = true;
    await getMinMaxFilters();
    await getCategories();
    await getCountries();
    await getPlaces(true);
    isRefresh.value = true;
  };

  const clearDraft = () => {
    basketStore.setDraftStatus(false);
    basketStore.setDraftId(0);
    clearBasket();
  };

  const isAuth = computed(() => {
    const token = ApiKey.get();
    return !!(token && token !== '' && token !== 'undefined');
  });
  const showScrollToTop = ref<boolean>(false);

  const handleScroll = () => {
    if (import.meta.client) {
      showScrollToTop.value =
        (window.scrollY || document.documentElement.scrollTop || 0) > 100;
    }
  };

  const scrollToTop = async () => {
    if (import.meta.client) {
      try {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        // Fallback для старых браузеров
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    }
  };

  onMounted(() => {
    // Инициализация localStorage-зависимых данных (только на клиенте)
    getBasket();
    getFilters();

    // Если пользователь не авторизован и у него активен черновик - очищаем черновик
    if (!isAuth.value && isDraft.value) {
      clearDraft();
    }

    if (isDraft.value && !isTemplate.value) {
      clearDraft();
    }
    initIntersectionObserver();
    initFilters();

    // Добавляем обработчик скролла
    window.addEventListener("scroll", handleScroll, { passive: true });
    // Проверяем текущую позицию скролла
    handleScroll();
  });

  onUnmounted(() => {
    if (import.meta.client) {
      window.removeEventListener("scroll", handleScroll);
    }
  });

  definePageMeta({
    layout: "data",
  });

  const getChannelWord = (count: number): string => {
    if (count % 10 === 1 && count % 100 !== 11) return "канал";
    if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20))
      return "канала";
    return "каналов";
  };
</script>

<style lang="scss" scoped>
  .price-select {
    .el-select {
      min-width: auto;
      width: auto;
      height: 30px !important;
      min-height: 30px !important;

      :deep(.el-select__wrapper) {
        border-radius: 8px !important;
        border: 0 !important;
        height: 30px !important;
        min-height: 30px !important;
        padding: 0 6px;
        max-width: 150px;
        background-color: #3176ff14 !important;

        &.is-disabled {
          background-color: #4292f41a !important;
          border: 1px solid #e3e8eb !important;
        }

        .el-select__selected-item.el-select__placeholder {
          position: relative;
          transform: unset;
          cursor: pointer;
        }

        .el-select__selected-item.el-select__placeholder span {
          font-size: 14px !important;
          cursor: pointer;
        }
      }

      :deep(.el-icon.el-select__caret.el-select__icon) {
        svg {
          width: 14px;
          height: 14px;

          path {
            fill: #556d80 !important;
          }
        }

        &:not(:last-child) {
          margin-bottom: 8px;
        }
      }
    }

    &.bg-light {
      :deep(.el-select__wrapper) {
        background-color: #f1f4f9 !important;
      }
    }
  }

  .el-select {
    :deep(.el-select__wrapper) {
      @apply border border-border;
    }
  }

  .price-select-bg {
    .el-select {
      :deep(.el-select__wrapper) {
        @apply border border-border !bg-primary/10;
      }
    }
  }

  .scroll-to-top-btn {
    @apply fixed right-4 z-50 w-12 h-12 bg-primary rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:bg-primary/90 hover:scale-110;
  }

  .scroll-to-top-enter-active,
  .scroll-to-top-leave-active {
    transition: all 0.3s ease;
  }

  .scroll-to-top-enter-from,
  .scroll-to-top-leave-to {
    opacity: 0;
    transform: translateY(20px) scale(0.8);
  }

  /* Скрытие скроллбара для слайдера категорий */
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  /* Стили для карточек категорий */
  @media (max-width: 768px) {
    .snap-x {
      scroll-padding-left: 0;
      scroll-padding-right: 0;
    }
  }

  // Поля фильтров по макету: серая подложка без рамок, скругление 12px
  // .el-select в селекторе — чтобы перебить !important из самих селектов
  .catalog-filters :deep(.el-select .el-select__wrapper) {
    min-height: 44px !important;
    border-radius: 12px !important;
    border-color: theme("colors.bgnew") !important;
    background-color: theme("colors.bgnew") !important;
    box-shadow: none !important;
  }

  // Мобильный триггер тех же селектов — обычный div с белой рамкой
  .catalog-filters :deep(.tree-select + .md\:hidden),
  .catalog-filters :deep(.multi-select + .md\:hidden) {
    @apply rounded-lg border-bgnew bg-bgnew text-xs;
  }

  .catalog-range {
    @apply rounded-xl border-bgnew;

    // Два поля «От» и «До» — скругляем только внешние углы, стык ровный
    :deep(input) {
      @apply rounded-none border-bgnew bg-bgnew;
    }

    :deep(> div:first-child input) {
      @apply rounded-l-xl;
    }

    :deep(> div:nth-child(2) input) {
      @apply rounded-r-xl;
    }
  }
</style>